/**
 * Changed-this-chapter markers — which panel section the last turn moved.
 *
 * This is nearly free: the `changes` array already says exactly what
 * moved, and the sections are collapsed by default because the panel is a status read rather
 * than a stat dump. Without the dot, a player whose Survival went 4 → 5 during a turn learns
 * about it only if they happen to open the skills section afterwards — the ticker said so, but
 * the ticker is twenty rows up the feed and gone by the next chapter.
 *
 * **A `quiet` change never marks anything**, and that reuses the live turn's ramp rather than inventing
 * a second rule. `time_advanced` fires every single turn; if the clock marked a section, that
 * section would be dotted permanently and the dot would come to mean nothing. The ticker
 * already decided which changes are news and which are bookkeeping, and this asks it.
 */
import type { Change, ChangeKind } from '@/api/generated';
import { weightOf } from '@/features/story/changeFamilies';

export const SECTION_IDS = ['inventory', 'injuries', 'effects', 'skills', 'places'] as const;
export type SectionId = (typeof SECTION_IDS)[number];

/**
 * Exhaustive over `ChangeKind` so a kind the API adds is a typecheck error rather than a
 * change that silently marks nothing (D5). `null` means "no collapsed section owns this".
 */
const SECTION_OF: Record<ChangeKind, SectionId | null> = {
  // The bars are above the fold and they moved on screen while the turn ran (the character panel's last
  // checkbox). A dot pointing at something the player just watched happen is noise.
  health: null,
  stamina: null,
  hunger: null,
  thirst: null,

  injury_added: 'injuries',
  injury_progressed: 'injuries',
  injury_treated: 'injuries',
  injury_healed: 'injuries',

  status_effect_added: 'effects',
  status_effect_removed: 'effects',

  skill_improved: 'skills',
  skill_learned: 'skills',

  moved: 'places',
  location_discovered: 'places',

  item_gained: 'inventory',
  item_consumed: 'inventory',
  item_dropped: 'inventory',
  // Points at the section like its three siblings, and marks nothing: `item_updated` is
  // `quiet` in the live turn's ramp — a round count ticking down is bookkeeping — and the `quiet`
  // filter above drops it before it gets here.
  item_updated: 'inventory',
  // A trade is an inventory event with a person attached: what left the pack and what
  // arrived are both in it, so it points where its three siblings point.
  traded: 'inventory',

  // The world panel's. It has no changed-this-chapter dot of its own — that is `TODO.md` §4.4's
  // deferred new-since-last-opened badge — so these stay null rather than marking a section
  // that would not draw one.
  location_updated: null,
  npc_introduced: null,
  npc_updated: null,
  npc_merged: null,
  faction_updated: null,
  fact_recorded: null,
  rumour_recorded: null,
  rumour_settled: null,

  // The feed's to announce, both of them.
  death: null,
  time_advanced: null,
  time_spent: null,
};

export function sectionOf(kind: ChangeKind): SectionId | null {
  return SECTION_OF[kind];
}

export function markedSections(changes: readonly Change[]): ReadonlySet<SectionId> {
  const marked = new Set<SectionId>();
  for (const change of changes) {
    if (weightOf(change) === 'quiet') continue;
    const section = sectionOf(change.kind);
    if (section !== null) marked.add(section);
  }
  return marked;
}
